import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Contrast, Type } from "lucide-react";
import { Separator } from "./ui/separator";
import { Switch } from "./ui/switch";
import { SectionCard } from "./identityForm";
import { TEXT_SIZES, textSizeOption, textSizeSummary } from "../lib/settingsSummary";
import { newUi } from "../lib/featureFlags";

/** Set the CSS custom property `index.css` sizes the whole app from. */
function applyTextSize(key) {
  document.documentElement.style.setProperty("--nw-base-font", textSizeOption(key).px);
}

function applyHighContrast(on) {
  document.documentElement.classList.toggle("hc-theme", !!on);
}

/**
 * "Text size" — the app-behaviour half of Settings: base font size and
 * high-contrast mode, both remembered on this device only.
 *
 * EXTRACTED FROM `pages/Settings.jsx` BY #456/#459, not rewritten. Every
 * testid, every string and the localStorage keys are the ones that page
 * carried; what changed is that the section folds. Settings.jsx was 614
 * lines serving both roles from one file and this slice added to it, so the
 * two sections that grew — this one and Notifications — moved out rather
 * than the file growing further past the point a reviewer can hold it.
 *
 * THE FOLD (#456 for the player, #459 for the coach — the same card, and
 * the same reason it is one component: this page has never had a coach
 * variant). It arrives CLOSED, which is what the responsive harness
 * requires of a registered fold (it opens each one, measures the panel, and
 * closes it again) and what makes a long settings page scannable. The
 * header therefore has to say what is inside it: `textSizeSummary` reports
 * the size in force, plus high contrast when that is on, because this card
 * folds away TWO settings and a badge naming only one of them is the
 * headerless-fold defect #378 was filed about, one level down.
 *
 * THE EFFECTS STAYED WITH THE CONTROLS. `applyTextSize` /
 * `applyHighContrast` run on mount here exactly as they ran on mount in
 * Settings.jsx — and neither is the primary applier: `public/index.html`
 * reads the same two localStorage keys in a blocking inline script, so the
 * choice is in force before this bundle parses. These keep the DOM in step
 * while the page is open (and if the fold is closed, the settings are still
 * applied — they are page state, not panel state).
 *
 * THIS CARD SURVIVES #797, WORDED FOR A MIXED APP. Settings is a shipped
 * screen, so the card and both its controls stay here exactly as they are —
 * #797 only retires the top bar's `AccessibilityMenu` "Aa" control, and only
 * on a REBUILT screen where a live check has actually proven it inert
 * (`frontend/e2e/text-size-tokens.spec.js`). High contrast is unaffected by
 * any of this (it still applies everywhere) and needs no change to its own
 * copy.
 *
 * THE `desc` CAVEAT, FIX ROUND (review, probe 3). Two defects in the first
 * cut, both from the review: "classic screens" / "rebuilt for the new
 * layout" were project jargon a player has no way to resolve into an actual
 * screen; and the caveat was shown UNCONDITIONALLY, so it described a
 * screen that does not exist on the production default (flag off) and,
 * per #797's fix round (B2), was untrue on the one screen it named even
 * with the flag on — `/onboarding` still moves (#830), so "the set-up
 * screen" would have been a specific, false claim.
 *
 * FIXED: the caveat is shown only when a screen whose control is ACTUALLY
 * retired is live on this device — today that is exactly
 * `newUi("coach-portal-empty")` (`CoachPortalEmptyState`, the one screen
 * `frontend/e2e/text-size-tokens.spec.js` proves inert), read fresh on
 * every render the same way every other `newUi(...)` call site in this app
 * is. In plain words, naming no project term: "a few redesigned screens"
 * and "your phone's zoom", not "classic"/"rebuilt". Off the flag (the
 * production default), the caveat is absent entirely and the description
 * is the pre-#797 wording minus "the whole app" — a claim this card can
 * still make in full for every screen it is actually true on.
 */
export default function TextSizeCard() {
  const [textSize, setTextSize] = useState(() => localStorage.getItem("nw_text_size") || "default");
  const [highContrast, setHighContrast] = useState(() => localStorage.getItem("nw_high_contrast") === "1");

  useEffect(() => { applyTextSize(textSize); }, [textSize]);
  useEffect(() => { applyHighContrast(highContrast); }, [highContrast]);

  const chooseTextSize = (key) => {
    setTextSize(key);
    localStorage.setItem("nw_text_size", key);
    applyTextSize(key);
    toast.success(`Text size set to ${textSizeOption(key).label}`);
  };

  const toggleHighContrast = (on) => {
    setHighContrast(on);
    localStorage.setItem("nw_high_contrast", on ? "1" : "0");
    applyHighContrast(on);
    toast.success(on ? "High-contrast mode on" : "High-contrast mode off");
  };

  return (
    <SectionCard
      icon={Type}
      usageItem="text_size"
      title="Text size"
      desc={
        newUi("coach-portal-empty")
          ? "Adjust how large text appears across the app. A few redesigned screens use a fixed text size instead — use your phone's zoom there. Your choice is remembered on this device."
          : "Adjust how large text appears across the app. Your choice is remembered on this device."
      }
      collapsible
      defaultOpen={false}
      summary={textSizeSummary(textSize, highContrast)}
      toggleTestid="settings-text-size-toggle"
      testid="settings-text-size-card"
    >
      <div className="grid sm:grid-cols-3 gap-3">
        {TEXT_SIZES.map((t) => {
          const active = textSize === t.key;
          return (
            <button
              key={t.key}
              onClick={() => chooseTextSize(t.key)}
              data-testid={`text-size-${t.key}`}
              className={`text-left rounded-xl border-2 p-4 transition-all ${
                active
                  ? "border-[#0B2E59] bg-[#0B2E59]/5"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-display font-bold text-slate-900" style={{ fontSize: t.px }}>
                  {t.label}
                </span>
                {active && (
                  <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#D4A017" }}>Active</span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-1">{t.desc}</div>
              <div className="text-[11px] font-mono text-slate-400 mt-1">{t.px}</div>
            </button>
          );
        })}
      </div>

      <Separator className="my-2" />

      <div className="flex items-start justify-between gap-4 pt-2">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#0B2E5915" }}>
            <Contrast size={16} color="#0B2E59" />
          </div>
          <div>
            <div className="font-display font-bold text-base text-slate-900">High-contrast mode</div>
            <div className="text-sm text-slate-500 mt-0.5">Boosts text to near-black, thickens borders and gives every focused control a bright gold outline. Pairs well with Large text size.</div>
          </div>
        </div>
        <Switch
          checked={highContrast}
          onCheckedChange={toggleHighContrast}
          data-testid="settings-high-contrast"
        />
      </div>
    </SectionCard>
  );
}
